import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import DOMPurify from "dompurify";
import { apiFetch, API_BASE, getSessionTokenValue } from "../../utils/apiClient";
import { publicAnonKey } from "../../../../utils/supabase/info";
import { useTemplateId } from "./TemplateContext";
import { useAuthSafe } from "./AuthContext";
import { cacheSet, cacheGetStale } from "../../utils/offlineCache";

// Re-export apiFetch for backward compat (used by activityApi, snapshotApi, fieldHistoryApi)
export { apiFetch };

/** Prefix used by snapshot keys — filtered out of normal content */
const SNAP_KEY_PREFIX = "__snap:";

/** Prefix used by template meta keys — filtered out of normal content */
const TPL_META_PREFIX = "__tpl:meta:";

/** Prefix used by activity log keys — filtered out of normal content */
const ACTIVITY_PREFIX = "__activity:";

/** Prefix used by field history keys — filtered out of normal content */
const FHIST_PREFIX = "__fhist:";

/**
 * Site-scoping helpers.
 * Every key sent to the server is prefixed with `{siteId}:` so that
 * multiple templates sharing the same Supabase database stay isolated.
 *
 * siteId is now DYNAMIC — provided by TemplateContext from the URL.
 */

/** Add site prefix to a local content key before sending to server */
export function scopeKey(key: string, siteId: string): string {
  return siteId + ":" + key;
}

/** Strip site prefix from a server key. Returns null if key belongs to another site. */
export function unscopeKey(key: string, siteId: string): string | null {
  const prefix = siteId + ":";
  if (key.startsWith(prefix)) return key.slice(prefix.length);
  return null;
}

/* ================================================================
   Undo / Redo types
   ================================================================ */

interface UndoEntry {
  key: string;
  oldValue: string | undefined;
  newValue: string;
  timestamp: number;
  /** Human-readable label describing the change */
  label: string;
}

const MAX_UNDO_STACK = 100;
/** If changes to the same key happen within this window (ms), merge them */
const MERGE_WINDOW_MS = 800;

/** Infer a human-readable label from a content key */
function inferUndoLabel(key: string): string {
  if (key.startsWith("blocks:")) return "\u05D1\u05DC\u05D5\u05E7\u05D9\u05DD";
  if (key.startsWith("nav:")) return "\u05EA\u05E4\u05E8\u05D9\u05D8";
  if (key.startsWith("seo:")) return "SEO";
  if (key.startsWith("hero:")) return "\u05D4\u05D9\u05E8\u05D5";
  if (key.startsWith("footer:")) return "\u05E4\u05D5\u05D8\u05E8";
  if (key.startsWith("cta:")) return "CTA";
  if (key.startsWith("contact:")) return "\u05D9\u05E6\u05D9\u05E8\u05EA \u05E7\u05E9\u05E8";
  if (key.includes(":title") || key.includes(":heading")) return "\u05DB\u05D5\u05EA\u05E8\u05EA";
  if (key.includes(":description") || key.includes(":text")) return "\u05D8\u05E7\u05E1\u05D8";
  if (key.includes(":image") || key.includes(":logo")) return "\u05EA\u05DE\u05D5\u05E0\u05D4";
  if (key.includes(":phone") || key.includes(":email")) return "\u05E4\u05E8\u05D8\u05D9 \u05E7\u05E9\u05E8";
  return "\u05EA\u05D5\u05DB\u05DF";
}

/* ================================================================
   Context types
   ================================================================ */

export type PreviewDevice = "desktop" | "tablet" | "mobile";

interface ContentEditingContextType {
  isEditMode: boolean;
  isPreviewMode: boolean;
  toggleEditMode: () => void;
  togglePreviewMode: () => void;
  getContent: (key: string, defaultValue: string) => string;
  saveContent: (key: string, value: string) => void;
  hasOverride: (key: string) => boolean;
  resetContent: (key: string) => void;
  resetAllContent: () => void;
  reloadContent: () => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
  pendingCount: number;
  saveError: string | null;
  /** True if any content was changed during the current edit session */
  hasChangedInSession: boolean;
  /** Current template/site ID (for passing to snapshotApi etc.) */
  currentSiteId: string;
  /* ——— Undo / Redo ——— */
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Label of the last action that can be undone */
  lastUndoLabel: string;
  /** Label of the last action that can be redone */
  lastRedoLabel: string;
  /** Recent undo history (last 20 items, newest first) */
  undoHistory: { label: string; timestamp: number }[];
  /* ——— Responsive Preview ——— */
  previewDevice: PreviewDevice;
  setPreviewDevice: (device: PreviewDevice) => void;
  /* ——— Content Key Registry (for completion meter) ——— */
  registerContentKey: (key: string) => void;
  unregisterContentKey: (key: string) => void;
  contentStats: { total: number; filled: number; percentage: number };
  /* ——— Overrides access (for Find & Replace) ——— */
  getAllOverrides: () => Record<string, string>;
}

const ContentEditingContext = createContext<ContentEditingContextType | null>(null);

export function useContentEditing() {
  const ctx = useContext(ContentEditingContext);
  if (!ctx) throw new Error("useContentEditing must be used within ContentEditingProvider");
  return ctx;
}

/** Safe version that returns null instead of throwing */
export function useContentEditingSafe(): ContentEditingContextType | null {
  return useContext(ContentEditingContext);
}

/** Hook for components: returns the display value for a content key */
export function useEditableContent(key: string, defaultValue: string): string {
  const ctx = useContext(ContentEditingContext);
  if (!ctx) return defaultValue;
  return ctx.getContent(key, defaultValue);
}

/**
 * Fire-and-forget fetch with `keepalive: true`.
 * Used during beforeunload / unmount so the browser keeps the connection
 * alive even after the page navigates away. This prevents the server-side
 * "Http: connection closed before message completed" error.
 */
function keepaliveFetch(path: string, body: string): void {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${publicAnonKey}`,
    };
    const token = getSessionTokenValue();
    if (token) {
      headers["X-Session-Token"] = token;
    }
    fetch(`${API_BASE}${path}`, {
      method: "PUT",
      keepalive: true,
      headers,
      body,
    }).catch(() => {});
  } catch {
    // Ignore — this fires during page teardown
  }
}

export function ContentEditingProvider({ children }: { children: React.ReactNode }) {
  const siteId = useTemplateId();
  const auth = useAuthSafe();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Synchronously seed overrides from stale cache so returning visitors
  // never see default placeholder text → eliminates CLS on warm loads.
  const cachedOverrides = useMemo(() => {
    if (!siteId) return {} as Record<string, string>;
    try {
      const stale = cacheGetStale<Record<string, string>>(`content_${siteId}`);
      if (stale && Object.keys(stale.data).length > 0) return stale.data;
    } catch { }
    return {} as Record<string, string>;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally once on mount

  const [overrides, setOverrides] = useState<Record<string, string>>(cachedOverrides);
  // Skip loading state (and PageSkeletonOverlay) when cache already has content.
  const [isLoading, setIsLoading] = useState<boolean>(Object.keys(cachedOverrides).length === 0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pendingRef = useRef<Record<string, string>>({});
  const [pendingCount, setPendingCount] = useState(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const [hasChangedInSession, setHasChangedInSession] = useState(false);

  /* ——— Undo / Redo state ——— */
  const undoStack = useRef<UndoEntry[]>([]);
  const redoStack = useRef<UndoEntry[]>([]);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  /* ——— Preview device ——— */
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("desktop");

  /* ——— Content Key Registry ——— */
  const registeredKeysRef = useRef<Set<string>>(new Set());
  const [registeredKeyCount, setRegisteredKeyCount] = useState(0);

  /** Mounted ref — used to skip state updates after unmount */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const registerContentKey = useCallback((key: string) => {
    if (!registeredKeysRef.current.has(key)) {
      registeredKeysRef.current.add(key);
      setRegisteredKeyCount(registeredKeysRef.current.size);
    }
  }, []);

  const unregisterContentKey = useCallback((key: string) => {
    if (registeredKeysRef.current.has(key)) {
      registeredKeysRef.current.delete(key);
      setRegisteredKeyCount(registeredKeysRef.current.size);
    }
  }, []);

  /* ================================================================
     Load content from server
     ================================================================ */

  /**
   * Load content from the server.
   *
   * Uses a `cancelled` ref-flag instead of AbortController.abort()
   * so the HTTP connection completes normally on the server side.
   * AbortController.abort() sends a TCP RST which causes the
   * "Http: connection closed before message completed" error on the
   * Deno Edge Function server.
   */
  const loadContent = useCallback(async (cancelledRef?: { current: boolean }) => {
    if (!siteId) return;
    setIsLoading(true);

    /** Cache key for this template's content */
    const cacheKey = `content_${siteId}`;

    try {
      // Consume hover-prefetch if available (started by TemplateCard/TemplateListRow
      // onMouseEnter), otherwise fall back to a fresh fetch.
      const prefetchKey = `__ZAP_CONTENT_${siteId}`;
      const pending = (window as any)[prefetchKey];
      let data: Record<string, any> | null = null;
      if (pending) {
        delete (window as any)[prefetchKey]; // consume once
        try { data = await pending; } catch { data = null; }
      }
      if (!data || typeof data !== "object") {
        data = await apiFetch(`/content/${encodeURIComponent(siteId)}`);
      }
      // If cancelled (e.g. unmount), don't update state
      if (cancelledRef?.current) return;
      if (!mountedRef.current) return;

      const raw: Record<string, any> = data.content || {};
      const result: Record<string, string> = {};
      for (const [serverKey, val] of Object.entries(raw)) {
        const localKey = unscopeKey(serverKey, siteId);
        if (
          localKey &&
          !localKey.startsWith(SNAP_KEY_PREFIX) &&
          !localKey.startsWith(TPL_META_PREFIX) &&
          !localKey.startsWith(ACTIVITY_PREFIX) &&
          !localKey.startsWith(FHIST_PREFIX) &&
          typeof val === "string"
        ) {
          result[localKey] = val;
        }
      }
      setOverrides(result);
      setSaveError(null);
      // Cache for offline / fast-load fallback (1 hour TTL)
      cacheSet(cacheKey, result, 60 * 60 * 1000);
    } catch (err) {
      // Silently ignore if cancelled / unmounted
      if (cancelledRef?.current) return;
      if (!mountedRef.current) return;

      // Fallback to stale offline cache
      const stale = cacheGetStale<Record<string, string>>(cacheKey);
      if (stale && Object.keys(stale.data).length > 0) {
        setOverrides(stale.data);
        setSaveError(null);
        console.warn(`[ContentEditing] Using cached content (age: ${Math.round(stale.age / 60_000)}min)`);
      } else {
        console.error("[ContentEditing] Failed to load content:", err);
        setSaveError(String(err));
      }
    } finally {
      if (!cancelledRef?.current && mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [siteId]);

  useEffect(() => {
    const cancelledRef = { current: false };
    loadContent(cancelledRef);
    return () => { cancelledRef.current = true; };
  }, [loadContent]);

  const reloadContent = useCallback(async () => {
    await loadContent();
  }, [loadContent]);

  /* ================================================================
     Get / Save / Override helpers
     ================================================================ */

  const getContent = useCallback(
    (key: string, defaultValue: string): string => {
      if (key in pendingRef.current) return pendingRef.current[key];
      if (key in overrides) return overrides[key];
      return defaultValue;
    },
    [overrides],
  );

  const hasOverride = useCallback(
    (key: string): boolean => {
      return key in overrides || key in pendingRef.current;
    },
    [overrides],
  );

  /* ——— Build the scoped payload for a batch of pending changes ——— */

  const buildBulkPayload = useCallback((batch: Record<string, string>): string => {
    const entries: Record<string, string> = {};
    for (const [k, v] of Object.entries(batch)) {
      entries[scopeKey(k, siteId)] = v;
    }
    return JSON.stringify({ overrides: entries });
  }, [siteId]);

  /* ——— Flush pending saves to server ——— */

  const flushPending = useCallback(async () => {
    const batch = { ...pendingRef.current };
    const keys = Object.keys(batch);
    if (keys.length === 0) return;

    pendingRef.current = {};
    setPendingCount(0);
    setIsSaving(true);

    // Optimistically apply to overrides
    setOverrides((prev) => ({ ...prev, ...batch }));

    try {
      const bodyStr = buildBulkPayload(batch);
      console.log("[ContentEditing] flushPending payload keys:", Object.keys(JSON.parse(bodyStr)), "entries count:", keys.length);

      await apiFetch("/content/bulk", {
        method: "PUT",
        body: bodyStr,
      });

      retryCountRef.current = 0; // Reset retry count on success
      setSaveError(null);
      // Update offline cache with latest overrides
      setOverrides((prev) => {
        cacheSet(`content_${siteId}`, prev, 60 * 60 * 1000);
        return prev;
      });
    } catch (err) {
      console.error("[ContentEditing] Failed to save batch:", err);
      setSaveError(String(err));

      // Put failed entries back in pending
      for (const [k, v] of Object.entries(batch)) {
        if (!(k in pendingRef.current)) {
          pendingRef.current[k] = v;
        }
      }
      setPendingCount(Object.keys(pendingRef.current).length);

      // Retry up to 3 times with exponential backoff, then stop
      retryCountRef.current++;
      if (retryCountRef.current <= 3) {
        const delay = 5000 * retryCountRef.current;
        console.warn(`[ContentEditing] Will retry in ${delay}ms (attempt ${retryCountRef.current}/3)`);
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          flushPending();
        }, delay);
      } else {
        console.error("[ContentEditing] Max retries reached. Giving up until next user save.");
        retryCountRef.current = 0;
      }
    } finally {
      setIsSaving(false);
    }
  }, [siteId, buildBulkPayload]);

  /**
   * Flush pending changes using `keepalive: true`.
   * Used during beforeunload and unmount so the browser keeps the
   * connection alive even after the page navigates away.
   */
  const flushPendingKeepalive = useCallback(() => {
    const batch = { ...pendingRef.current };
    if (Object.keys(batch).length === 0) return;
    pendingRef.current = {};
    const bodyStr = buildBulkPayload(batch);
    keepaliveFetch("/content/bulk", bodyStr);
  }, [buildBulkPayload]);

  const saveContent = useCallback(
    (key: string, value: string) => {
      // Sanitize HTML content to prevent XSS — but only if the value
      // actually contains HTML-like content. Plain text (including emojis,
      // JSON, numbers) can skip sanitization for safety and performance.
      if (value.includes("<")) {
        const sanitized = DOMPurify.sanitize(value, {
          ALLOWED_TAGS: [
            "b", "i", "u", "em", "strong", "a", "br", "p", "span", "div",
            "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
            "blockquote", "code", "pre", "sub", "sup", "mark", "del", "ins",
            "table", "thead", "tbody", "tr", "th", "td", "img", "figure", "figcaption",
            "font",
          ],
          ALLOWED_ATTR: [
            "href", "target", "rel", "src", "alt", "title", "class", "style",
            "width", "height", "id", "colspan", "rowspan", "color", "size", "face",
          ],
          ALLOW_DATA_ATTR: false,
        });
        value = sanitized;
      }

      // Push undo entry
      const oldValue = key in pendingRef.current ? pendingRef.current[key] : overrides[key];
      const now = Date.now();
      const lastUndo = undoStack.current[undoStack.current.length - 1];

      if (
        lastUndo &&
        lastUndo.key === key &&
        now - lastUndo.timestamp < MERGE_WINDOW_MS
      ) {
        // Merge with last undo entry (same key, close in time)
        lastUndo.newValue = value;
        lastUndo.timestamp = now;
      } else {
        undoStack.current.push({ key, oldValue, newValue: value, timestamp: now, label: inferUndoLabel(key) });
        if (undoStack.current.length > MAX_UNDO_STACK) {
          undoStack.current.shift();
        }
      }
      // Clear redo on new change
      redoStack.current = [];
      setUndoCount(undoStack.current.length);
      setRedoCount(0);

      // Queue for save
      pendingRef.current[key] = value;
      setPendingCount(Object.keys(pendingRef.current).length);
      setHasChangedInSession(true);

      // Also update overrides immediately for fast UI
      setOverrides((prev) => ({ ...prev, [key]: value }));

      // Debounce flush
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        flushPending();
      }, 1200);
    },
    [overrides, flushPending],
  );

  const resetContent = useCallback(
    (key: string) => {
      // Remove from overrides and pending
      delete pendingRef.current[key];
      setPendingCount(Object.keys(pendingRef.current).length);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      // Delete from server (empty string triggers server-side delete)
      apiFetch("/content/bulk", {
        method: "PUT",
        body: JSON.stringify({
          overrides: { [scopeKey(key, siteId)]: "" },
        }),
      }).catch((err) =>
        console.error("[ContentEditing] Failed to delete content key:", err),
      );
    },
    [siteId],
  );

  const resetAllContent = useCallback(() => {
    const keys = Object.keys(overrides);
    if (keys.length === 0) return;

    pendingRef.current = {};
    setPendingCount(0);
    setOverrides({});

    // Delete all from server (empty string triggers server-side delete)
    apiFetch("/content/bulk", {
      method: "PUT",
      body: JSON.stringify({
        overrides: Object.fromEntries(
          keys.map((k) => [scopeKey(k, siteId), ""]),
        ),
      }),
    }).catch((err) =>
      console.error("[ContentEditing] Failed to reset all content:", err),
    );
  }, [overrides, siteId]);

  /* ================================================================
     Undo / Redo
     ================================================================ */

  const undo = useCallback(() => {
    const entry = undoStack.current.pop();
    if (!entry) return;

    redoStack.current.push({
      key: entry.key,
      oldValue: entry.newValue,
      newValue: entry.oldValue || "",
      timestamp: Date.now(),
      label: entry.label,
    });

    setUndoCount(undoStack.current.length);
    setRedoCount(redoStack.current.length);

    // Restore old value
    if (entry.oldValue !== undefined) {
      pendingRef.current[entry.key] = entry.oldValue;
      setOverrides((prev) => ({ ...prev, [entry.key]: entry.oldValue! }));
    } else {
      delete pendingRef.current[entry.key];
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[entry.key];
        return next;
      });
    }
    setPendingCount(Object.keys(pendingRef.current).length);

    // Flush the change
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushPending();
    }, 800);
  }, [flushPending]);

  const redo = useCallback(() => {
    const entry = redoStack.current.pop();
    if (!entry) return;

    undoStack.current.push({
      key: entry.key,
      oldValue: entry.oldValue,
      newValue: entry.newValue,
      timestamp: Date.now(),
      label: entry.label,
    });

    setUndoCount(undoStack.current.length);
    setRedoCount(redoStack.current.length);

    // Apply new value
    pendingRef.current[entry.key] = entry.newValue;
    setOverrides((prev) => ({ ...prev, [entry.key]: entry.newValue }));
    setPendingCount(Object.keys(pendingRef.current).length);

    // Flush the change
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushPending();
    }, 800);
  }, [flushPending]);

  /* ================================================================
     Toggle edit mode
     ================================================================ */

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      const willBeEditMode = !prev;

      if (!willBeEditMode) {
        // Exiting edit mode — flush + stamp lastEditedBy + auto-snapshot
        const doFlushAndSnapshot = async () => {
          await flushPending();

          // Stamp lastEditedBy on template meta (fire-and-forget)
          if (hasChangedInSession && auth?.user) {
            try {
              const { updateTemplate } = await import("../../utils/templateApi");
              await updateTemplate(siteId, { lastEditedBy: auth.user.displayName || auth.user.username });
            } catch (err) {
              console.error("[ContentEditing] Failed to stamp lastEditedBy:", err);
            }
          }

          // Auto-snapshot on exit — safety net so users can always recover
          if (hasChangedInSession) {
            try {
              const { createSnapshot } = await import("../../utils/snapshotApi");
              await createSnapshot(siteId, "", "auto-exit");
              console.log("[ContentEditing] Auto-snapshot created on edit-mode exit");
            } catch (err) {
              // Non-fatal — don't block the user from exiting
              console.warn("[ContentEditing] Auto-snapshot failed (non-fatal):", err);
            }
          }

          setHasChangedInSession(false);
        };
        doFlushAndSnapshot();
      }

      return willBeEditMode;
    });
  }, [flushPending, hasChangedInSession, auth, siteId]);

  const togglePreviewMode = useCallback(() => {
    setIsPreviewMode((prev) => !prev);
  }, []);

  /* ================================================================
     Flush before page unload — use keepalive to prevent connection drops
     ================================================================ */

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (Object.keys(pendingRef.current).length > 0) {
        // Use keepalive so the browser finishes sending the request
        // even after the page starts unloading
        flushPendingKeepalive();
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [flushPendingKeepalive]);

  // Cleanup timers on unmount — use keepalive for final flush
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (Object.keys(pendingRef.current).length > 0) {
        // Use keepalive so the request survives component unmount
        flushPendingKeepalive();
      }
    };
  }, [flushPendingKeepalive]);

  /* ================================================================
     Computed: content completion stats
     ================================================================ */
  const contentStats = useMemo(() => {
    const total = registeredKeyCount;
    if (total === 0) return { total: 0, filled: 0, percentage: 0 };
    let filled = 0;
    for (const key of registeredKeysRef.current) {
      if (key in overrides) filled++;
    }
    const percentage = Math.round((filled / total) * 100);
    return { total, filled, percentage };
  }, [registeredKeyCount, overrides]);

  /* ================================================================
     getAllOverrides — expose all stored overrides (for Find & Replace)
     ================================================================ */
  const getAllOverrides = useCallback(() => ({ ...overrides }), [overrides]);

  return (
    <ContentEditingContext.Provider
      value={{
        isEditMode: isEditMode && !isPreviewMode,
        isPreviewMode,
        toggleEditMode,
        togglePreviewMode,
        getContent,
        saveContent,
        hasOverride,
        resetContent,
        resetAllContent,
        reloadContent,
        isLoading,
        isSaving,
        pendingCount,
        saveError,
        hasChangedInSession,
        currentSiteId: siteId,
        undo,
        redo,
        canUndo: undoCount > 0,
        canRedo: redoCount > 0,
        lastUndoLabel: undoStack.current.length > 0 ? undoStack.current[undoStack.current.length - 1].label : "",
        lastRedoLabel: redoStack.current.length > 0 ? redoStack.current[redoStack.current.length - 1].label : "",
        undoHistory: undoStack.current.slice(-20).map(entry => ({ label: entry.label, timestamp: entry.timestamp })).reverse(),
        previewDevice,
        setPreviewDevice,
        registerContentKey,
        unregisterContentKey,
        contentStats,
        getAllOverrides,
      }}
    >
      {children}
    </ContentEditingContext.Provider>
  );
}